import React, { useMemo } from "react";
import { useTranslation } from "@/lib/i18n";

import { Button, Checkbox, Menu, Text, TextInput, View } from "@/components/common";
import { useThemedStyles, type DesignTokens } from "../../hooks/useDesignTokens";

export type FormSchemaRendererOption = { label: string; value: string };

export type FormSchemaRendererField = {
  field_key: string;
  label?: string;
  field_type?: string;
  required?: boolean;
  help_text?: string;
  placeholder?: string;
  options?: FormSchemaRendererOption[];
  // Embedded form (DB-backed) shape
  name?: string;
  type?: string;
  helperText?: string;
  defaultValue?: any;
  disabled?: boolean;
  options_json?: any;
  validation_json?: any;
  validation?: {
    options?: FormSchemaRendererOption[];
    json_schema?: any;
    [key: string]: any;
  };
};

export type FormSchemaRendererSchema = {
  fields?: FormSchemaRendererField[];
  data?: {
    fields?: FormSchemaRendererField[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type FormSchemaRendererProps = {
  schema: FormSchemaRendererSchema | null | undefined;
  values: Record<string, any>;
  onChangeValues: (nextValues: Record<string, any>) => void;
  disabled?: boolean;
  testIDPrefix?: string;
  errors?: Record<string, string>;
  onChangeField?: (fieldKey: string, nextValue: any) => void;
  renderField?: (
    args: {
      field: FormSchemaRendererField;
      fieldKey: string;
      type: string;
      required: boolean;
      label: string;
      value: any;
      setValue: (next: any) => void;
      disabled: boolean;
      testIDPrefix: string;
    },
  ) => React.ReactNode | null | undefined;
};

function isChoiceFieldType(fieldType: string): boolean {
  const t = String(fieldType ?? "").toLowerCase();
  return (
    t === "radio" ||
    t === "select" ||
    t === "checkbox_group" ||
    t === "multiselect"
  );
}

function isJsonFieldType(fieldType: string): boolean {
  return String(fieldType ?? "").toLowerCase() === "json";
}

function isPhoneFieldType(fieldType: string): boolean {
  const t = String(fieldType ?? "").toLowerCase();
  return t === "phone" || t === "tel";
}

function isDateTimeFieldType(fieldType: string): boolean {
  const t = String(fieldType ?? "").toLowerCase();
  return t === "datetime" || t === "date_time";
}

function isAddressLikeFieldKey(fieldKey: string): boolean {
  const k = String(fieldKey ?? "").toLowerCase();
  return k === "address" || k.endsWith("_address") || k.includes("address");
}

function extractOptions(field: any): FormSchemaRendererOption[] {
  const tryParseJson = (value: any): any => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const directRaw = field?.options ?? field?.options_json;
  const nestedRaw = field?.validation?.options ?? field?.validation_json?.options;

  const direct = tryParseJson(directRaw);
  const nested = tryParseJson(nestedRaw);

  const options = Array.isArray(direct)
    ? direct
    : Array.isArray(nested)
      ? nested
      : undefined;
  if (!Array.isArray(options)) return [];

  return options
    .map((o: any) => ({
      label: String(o?.label ?? o?.value ?? ""),
      value: String(o?.value ?? o?.label ?? ""),
    }))
    .filter((o: any) => o.label && o.value);
}

function extractJsonSchema(field: any): any | undefined {
  const tryParseJson = (value: any): any => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const direct = field?.json_schema ?? field?.jsonSchema;
  const nested = field?.validation?.json_schema ?? field?.validation?.jsonSchema;
  const nestedJson = field?.validation_json?.json_schema ?? field?.validation_json?.jsonSchema;
  const picked = nested ?? nestedJson ?? direct;
  const parsed = tryParseJson(picked);
  return parsed && typeof parsed === "object" ? parsed : undefined;
}

function isAddressSchema(jsonSchema: any): boolean {
  if (!jsonSchema || typeof jsonSchema !== "object") return false;
  if (String(jsonSchema.type ?? "").toLowerCase() !== "object") return false;
  const fields = Array.isArray(jsonSchema.fields) ? jsonSchema.fields : [];
  const keys = new Set(fields.map((f: any) => String(f?.field_key ?? f?.name ?? "").toLowerCase()));
  const requiredKeys = ["street", "suburb", "city", "state", "postcode", "country"];
  return requiredKeys.every((k) => keys.has(k));
}

export default function FormSchemaRenderer({
  schema,
  values,
  onChangeValues,
  disabled = false,
  testIDPrefix = "form-schema",
  errors,
  onChangeField,
  renderField,
}: FormSchemaRendererProps) {
  const { t } = useTranslation();

  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({});

  const styles = useThemedStyles((tokens: DesignTokens) => ({
    fieldGroup: ({ gap: tokens.spacing.sm } as unknown) as any,
    checkboxRow:
      ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: tokens.spacing.sm,
        flexWrap: "wrap" as const,
      } as unknown) as any,
    row:
      ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        justifyContent: "space-between" as const,
        flexWrap: "wrap" as const,
        gap: tokens.spacing.sm,
      } as unknown) as any,
    choiceOptions: ({ gap: tokens.spacing.xs } as unknown) as any,
    helperText:
      ({
        color: tokens.colors.mutedText,
        fontSize: 13,
        lineHeight: 18,
      } as unknown) as any,
    errorText:
      ({
        color: tokens.colors.error,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: "500" as const,
      } as unknown) as any,
  }));

  const fields = useMemo(() => {
    const direct = (schema as any)?.fields;
    const nested = (schema as any)?.data?.fields;
    const list = Array.isArray(direct) ? direct : Array.isArray(nested) ? nested : undefined;
    return Array.isArray(list) ? (list as FormSchemaRendererField[]) : [];
  }, [schema]);

  const setValue = (fieldKey: string, value: any) => {
    const next = { ...values, [fieldKey]: value };
    onChangeValues(next);
    onChangeField?.(fieldKey, value);
  };

  const setObjectField = (fieldKey: string, subKey: string, value: any) => {
    const existing = values[fieldKey];
    const base =
      existing && typeof existing === "object" && !Array.isArray(existing)
        ? existing
        : {};

    const next = {
      ...values,
      [fieldKey]: {
        ...base,
        [subKey]: value,
      },
    };
    onChangeValues(next);
    onChangeField?.(fieldKey, next[fieldKey]);
  };

  const setArrayItemValue = (fieldKey: string, index: number, nextValue: any) => {
    const existing = values[fieldKey];
    const base = Array.isArray(existing) ? existing : [];
    const next = base.slice();
    next[index] = nextValue;
    setValue(fieldKey, next);
  };

  const setArrayItemObjectField = (fieldKey: string, index: number, subKey: string, nextValue: any) => {
    const existing = values[fieldKey];
    const base = Array.isArray(existing) ? existing : [];
    const currentItem = base[index];
    const currentObj =
      currentItem && typeof currentItem === "object" && !Array.isArray(currentItem) ? currentItem : {};
    const next = base.slice();
    next[index] = { ...currentObj, [subKey]: nextValue };
    setValue(fieldKey, next);
  };

  const addArrayItem = (fieldKey: string, initial: any) => {
    const existing = values[fieldKey];
    const base = Array.isArray(existing) ? existing : [];
    setValue(fieldKey, [...base, initial]);
  };

  const removeArrayItem = (fieldKey: string, index: number) => {
    const existing = values[fieldKey];
    const base = Array.isArray(existing) ? existing : [];
    setValue(
      fieldKey,
      base.filter((_, i) => i !== index),
    );
  };

  const renderScalarInput = (opts: {
    fieldKey: string;
    type: string;
    label: string;
    required: boolean;
    value: any;
    placeholder?: string;
    onChange: (next: any) => void;
    testID: string;
  }) => {
    const type = String(opts.type ?? "text").toLowerCase();

    if (type === "boolean" || type === "checkbox") {
      return (
        <View key={opts.testID} style={styles.checkboxRow}>
          <Checkbox
            testID={opts.testID}
            status={opts.value ? "checked" : "unchecked"}
            disabled={disabled}
            onPress={() => opts.onChange(!opts.value)}
          />
          <Text>
            {opts.label}
            {opts.required ? " *" : ""}
          </Text>
        </View>
      );
    }

    if (type === "number" || type === "integer") {
      return (
        <TextInput
          key={opts.testID}
          testID={opts.testID}
          label={opts.label + (opts.required ? " *" : "")}
          mode="outlined"
          disabled={disabled}
          value={String(opts.value ?? "")}
          onChangeText={(text) => opts.onChange(text)}
          keyboardType="numeric"
          placeholder={opts.placeholder}
        />
      );
    }

    if (type === "email" || isPhoneFieldType(type) || type === "date" || isDateTimeFieldType(type)) {
      const keyboardType =
        type === "email"
          ? "email-address"
          : isPhoneFieldType(type)
            ? "phone-pad"
            : "default";

      const placeholder =
        opts.placeholder ??
        (type === "date" ? "YYYY-MM-DD" : isDateTimeFieldType(type) ? "YYYY-MM-DDTHH:MM" : undefined);

      return (
        <TextInput
          key={opts.testID}
          testID={opts.testID}
          label={opts.label + (opts.required ? " *" : "")}
          mode="outlined"
          disabled={disabled}
          value={String(opts.value ?? "")}
          onChangeText={(text) => opts.onChange(text)}
          keyboardType={keyboardType as any}
          autoCapitalize={type === "email" ? "none" : "sentences"}
          placeholder={placeholder}
        />
      );
    }

    return (
      <TextInput
        key={opts.testID}
        testID={opts.testID}
        label={opts.label + (opts.required ? " *" : "")}
        mode="outlined"
        disabled={disabled}
        value={String(opts.value ?? "")}
        onChangeText={(text) => opts.onChange(text)}
        multiline={type === "textarea"}
        placeholder={opts.placeholder}
      />
    );
  };

  const renderJsonSchemaField = (fieldKey: string, label: string, required: boolean, jsonSchema: any) => {
    const schemaType = String(jsonSchema?.type ?? "").toLowerCase();

    if (schemaType === "object") {
      const existing = values[fieldKey];
      const obj = existing && typeof existing === "object" && !Array.isArray(existing) ? existing : {};
      const fields = Array.isArray(jsonSchema.fields) ? jsonSchema.fields : [];

      // Use the nicer address layout when schema matches the canonical address.
      if (isAddressLikeFieldKey(fieldKey) && isAddressSchema(jsonSchema)) {
        return (
          <View key={fieldKey} style={styles.fieldGroup}>
            <Text>
              {label}
              {required ? " *" : ""}
            </Text>

            <TextInput
              testID={`${testIDPrefix}-address-${fieldKey}-street`}
              label={t("cms.forms.preview.address.street", "Street address")}
              mode="outlined"
              disabled={disabled}
              value={String((obj as any).street ?? (obj as any).line1 ?? "")}
              onChangeText={(text) => setObjectField(fieldKey, "street", text)}
            />
            <TextInput
              testID={`${testIDPrefix}-address-${fieldKey}-unit`}
              label={t("cms.forms.preview.address.unit", "Unit / Apt (optional)")}
              mode="outlined"
              disabled={disabled}
              value={String((obj as any).unit ?? (obj as any).line2 ?? "")}
              onChangeText={(text) => setObjectField(fieldKey, "unit", text)}
            />

            <View style={styles.row}>
              <View style={{ flex: 1 } as any}>
                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-city`}
                  label={t("cms.forms.preview.address.city", "City")}
                  mode="outlined"
                  disabled={disabled}
                  value={String((obj as any).city ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "city", text)}
                />
              </View>
              <View style={{ flex: 1 } as any}>
                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-state`}
                  label={t("cms.forms.preview.address.state", "State / Region")}
                  mode="outlined"
                  disabled={disabled}
                  value={String((obj as any).state ?? (obj as any).region ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "state", text)}
                />
              </View>
            </View>

            <View style={styles.row}>
              <View style={{ flex: 1 } as any}>
                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-postcode`}
                  label={t("cms.forms.preview.address.postcode", "Postcode")}
                  mode="outlined"
                  disabled={disabled}
                  value={String((obj as any).postcode ?? (obj as any).zip ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "postcode", text)}
                />
              </View>
              <View style={{ flex: 1 } as any}>
                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-country`}
                  label={t("cms.forms.preview.address.country", "Country")}
                  mode="outlined"
                  disabled={disabled}
                  value={String((obj as any).country ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "country", text)}
                />
              </View>
            </View>
          </View>
        );
      }

      return (
        <View key={fieldKey} style={styles.fieldGroup}>
          <Text>
            {label}
            {required ? " *" : ""}
          </Text>
          {fields.map((f: any) => {
            const subKey = String(f?.field_key ?? f?.name ?? "").trim();
            if (!subKey) return null;
            const subType = String(f?.field_type ?? f?.type ?? "text").toLowerCase();
            const subLabel = String(f?.label ?? f?.field_label ?? f?.fieldLabel ?? subKey);
            const subRequired = Boolean(f?.required ?? f?.is_required ?? f?.isRequired ?? false);
            const subSchema = extractJsonSchema(f);

            if (isJsonFieldType(subType) && subSchema) {
              // Nested json inside object: store nested value as object/array at subKey.
              const nestedKey = `${fieldKey}.${subKey}`;
              // For simplicity, render nested json as a JSON textarea.
              // (The registry currently only uses nested json arrays of text for availability.)
              return (
                <TextInput
                  key={nestedKey}
                  testID={`${testIDPrefix}-json-${fieldKey}-${subKey}`}
                  label={subLabel + (subRequired ? " *" : "")}
                  mode="outlined"
                  disabled={disabled}
                  multiline
                  value={
                    typeof (obj as any)[subKey] === "string"
                      ? (obj as any)[subKey]
                      : JSON.stringify((obj as any)[subKey] ?? (subSchema.type === "array" ? [] : {}), null, 2)
                  }
                  onChangeText={(text) => setObjectField(fieldKey, subKey, text)}
                  placeholder={t("cms.forms.preview.jsonPlaceholder", "JSON object")}
                />
              );
            }

            return renderScalarInput({
              fieldKey: `${fieldKey}.${subKey}`,
              type: subType,
              label: subLabel,
              required: subRequired,
              value: (obj as any)[subKey],
              placeholder: f?.placeholder,
              onChange: (next) => setObjectField(fieldKey, subKey, next),
              testID: `${testIDPrefix}-object-${fieldKey}-${subKey}`,
            });
          })}
        </View>
      );
    }

    if (schemaType === "array") {
      const existing = values[fieldKey];
      const arr = Array.isArray(existing) ? existing : [];

      const items = jsonSchema.items;
      const itemsType = String(items?.type ?? "").toLowerCase();
      const itemsFieldType = String(jsonSchema.items_field_type ?? jsonSchema.itemsFieldType ?? "").toLowerCase();

      // Array of objects
      if (itemsType === "object" && Array.isArray(items?.fields)) {
        const itemFields = items.fields;
        return (
          <View key={fieldKey} style={styles.fieldGroup}>
            <Text>
              {label}
              {required ? " *" : ""}
            </Text>

            {arr.map((item: any, index: number) => {
              const obj = item && typeof item === "object" && !Array.isArray(item) ? item : {};
              return (
                <View key={`${fieldKey}-${index}`} style={styles.fieldGroup}>
                  <View style={styles.row}>
                    <Text>{t("cms.forms.array.item", `Item ${index + 1}`)}</Text>
                    <Button
                      mode="text"
                      disabled={disabled}
                      onPress={() => removeArrayItem(fieldKey, index)}
                    >
                      {t("cms.forms.array.remove", "Remove")}
                    </Button>
                  </View>

                  {itemFields.map((f: any) => {
                    const subKey = String(f?.field_key ?? f?.name ?? "").trim();
                    if (!subKey) return null;
                    const subType = String(f?.field_type ?? f?.type ?? "text").toLowerCase();
                    const subLabel = String(f?.label ?? f?.field_label ?? f?.fieldLabel ?? subKey);
                    const subRequired = Boolean(f?.required ?? f?.is_required ?? f?.isRequired ?? false);

                    if (subType === "boolean" || subType === "checkbox") {
                      return (
                        <View key={`${fieldKey}-${index}-${subKey}`} style={styles.checkboxRow}>
                          <Checkbox
                            testID={`${testIDPrefix}-array-${fieldKey}-${index}-${subKey}`}
                            status={(obj as any)[subKey] ? "checked" : "unchecked"}
                            disabled={disabled}
                            onPress={() =>
                              setArrayItemObjectField(
                                fieldKey,
                                index,
                                subKey,
                                !(obj as any)[subKey],
                              )
                            }
                          />
                          <Text>
                            {subLabel}
                            {subRequired ? " *" : ""}
                          </Text>
                        </View>
                      );
                    }

                    return (
                      <TextInput
                        key={`${fieldKey}-${index}-${subKey}`}
                        testID={`${testIDPrefix}-array-${fieldKey}-${index}-${subKey}`}
                        label={subLabel + (subRequired ? " *" : "")}
                        mode="outlined"
                        disabled={disabled}
                        value={String((obj as any)[subKey] ?? "")}
                        onChangeText={(text) =>
                          setArrayItemObjectField(fieldKey, index, subKey, text)
                        }
                        multiline={subType === "textarea"}
                        keyboardType={
                          subType === "email"
                            ? ("email-address" as any)
                            : isPhoneFieldType(subType)
                              ? ("phone-pad" as any)
                              : subType === "number" || subType === "integer"
                                ? ("numeric" as any)
                                : ("default" as any)
                        }
                      />
                    );
                  })}
                </View>
              );
            })}

            <Button
              mode="outlined"
              disabled={disabled}
              onPress={() => addArrayItem(fieldKey, {})}
            >
              {t("cms.forms.array.add", "Add")}
            </Button>
          </View>
        );
      }

      // Array of scalar values (e.g., tags/medications)
      if (itemsFieldType) {
        const scalarType = itemsFieldType;
        return (
          <View key={fieldKey} style={styles.fieldGroup}>
            <Text>
              {label}
              {required ? " *" : ""}
            </Text>

            {arr.map((item: any, index: number) => (
              <View key={`${fieldKey}-${index}`} style={styles.row}>
                <View style={{ flex: 1 } as any}>
                  <TextInput
                    testID={`${testIDPrefix}-array-${fieldKey}-${index}`}
                    label={t("cms.forms.array.value", "Value")}
                    mode="outlined"
                    disabled={disabled}
                    value={String(item ?? "")}
                    onChangeText={(text) => setArrayItemValue(fieldKey, index, text)}
                    multiline={scalarType === "textarea"}
                  />
                </View>
                <Button
                  mode="text"
                  disabled={disabled}
                  onPress={() => removeArrayItem(fieldKey, index)}
                >
                  {t("cms.forms.array.remove", "Remove")}
                </Button>
              </View>
            ))}

            <Button
              mode="outlined"
              disabled={disabled}
              onPress={() => addArrayItem(fieldKey, "")}
            >
              {t("cms.forms.array.add", "Add")}
            </Button>
          </View>
        );
      }

      // Fallback: JSON textarea
      return (
        <TextInput
          key={fieldKey}
          testID={`${testIDPrefix}-field-${fieldKey}`}
          label={label + (required ? " *" : "")}
          mode="outlined"
          disabled={disabled}
          multiline
          value={
            typeof values[fieldKey] === "string"
              ? values[fieldKey]
              : JSON.stringify(values[fieldKey] ?? [], null, 2)
          }
          onChangeText={(text) => setValue(fieldKey, text)}
          placeholder={t("cms.forms.preview.jsonPlaceholder", "JSON object")}
        />
      );
    }

    return undefined;
  };

  return (
    <View style={styles.fieldGroup}>
      {fields.map((raw) => {
        const rawAny = raw as any;

        const fieldKey = String(
          rawAny?.field_key ??
            rawAny?.fieldKey ??
            rawAny?.key ??
            rawAny?.name ??
            "",
        );
        if (!fieldKey) return null;

        const type = String(
          rawAny?.field_type ??
            rawAny?.fieldType ??
            rawAny?.type ??
            "text",
        ).toLowerCase();
        const required = !!(
          rawAny?.required ?? rawAny?.is_required ?? rawAny?.isRequired
        );
        const label = String(
          rawAny?.label ?? rawAny?.field_label ?? rawAny?.fieldLabel ?? fieldKey,
        );

        const helperText = String(
          rawAny?.help_text ?? rawAny?.helperText ?? "",
        ).trim();

        const helperTextLower = helperText.toLowerCase();
        const isAddressJsonHint = helperTextLower.includes("json object");

        const errorText = String(errors?.[fieldKey] ?? "").trim();

        const overridden = renderField?.({
          field: raw,
          fieldKey,
          type,
          required,
          label,
          value: values[fieldKey],
          setValue: (next) => setValue(fieldKey, next),
          disabled,
          testIDPrefix,
        });

        if (overridden !== undefined) {
          return <React.Fragment key={fieldKey}>{overridden}</React.Fragment>;
        }

        const jsonSchema = extractJsonSchema(rawAny);

        if (isJsonFieldType(type) && jsonSchema) {
          const rendered = renderJsonSchemaField(fieldKey, label, required, jsonSchema);
          if (rendered) {
            return (
              <View key={fieldKey} style={styles.fieldGroup}>
                {rendered}
                {helperText ? <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text> : null}
                {errorText ? <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text> : null}
              </View>
            );
          }
        }

        if (isAddressLikeFieldKey(fieldKey) && (isJsonFieldType(type) || isAddressJsonHint || isAddressSchema(jsonSchema))) {
            const existing = values[fieldKey];
            const obj =
              existing && typeof existing === "object" && !Array.isArray(existing)
                ? existing
                : {};

            return (
              <View key={fieldKey} style={styles.fieldGroup}>
                <Text>
                  {label}
                  {required ? " *" : ""}
                </Text>

                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-street`}
                  label={t("cms.forms.preview.address.street", "Street address")}
                  mode="outlined"
                  disabled={disabled}
                  value={String(obj.street ?? obj.line1 ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "street", text)}
                />
                <TextInput
                  testID={`${testIDPrefix}-address-${fieldKey}-unit`}
                  label={t(
                    "cms.forms.preview.address.unit",
                    "Unit / Apt (optional)",
                  )}
                  mode="outlined"
                  disabled={disabled}
                  value={String(obj.unit ?? obj.line2 ?? "")}
                  onChangeText={(text) => setObjectField(fieldKey, "unit", text)}
                />

                <View style={styles.row}>
                  <View style={{ flex: 1 } as any}>
                    <TextInput
                      testID={`${testIDPrefix}-address-${fieldKey}-city`}
                      label={t("cms.forms.preview.address.city", "City")}
                      mode="outlined"
                      disabled={disabled}
                      value={String(obj.city ?? "")}
                      onChangeText={(text) => setObjectField(fieldKey, "city", text)}
                    />
                  </View>
                  <View style={{ flex: 1 } as any}>
                    <TextInput
                      testID={`${testIDPrefix}-address-${fieldKey}-state`}
                      label={t(
                        "cms.forms.preview.address.state",
                        "State / Region",
                      )}
                      mode="outlined"
                      disabled={disabled}
                      value={String(obj.state ?? obj.region ?? "")}
                      onChangeText={(text) => setObjectField(fieldKey, "state", text)}
                    />
                  </View>
                </View>

                <View style={styles.row}>
                  <View style={{ flex: 1 } as any}>
                    <TextInput
                      testID={`${testIDPrefix}-address-${fieldKey}-postcode`}
                      label={t(
                        "cms.forms.preview.address.postcode",
                        "Postcode",
                      )}
                      mode="outlined"
                      disabled={disabled}
                      value={String(obj.postcode ?? obj.zip ?? "")}
                      onChangeText={(text) =>
                        setObjectField(fieldKey, "postcode", text)
                      }
                    />
                  </View>
                  <View style={{ flex: 1 } as any}>
                    <TextInput
                      testID={`${testIDPrefix}-address-${fieldKey}-country`}
                      label={t(
                        "cms.forms.preview.address.country",
                        "Country",
                      )}
                      mode="outlined"
                      disabled={disabled}
                      value={String(obj.country ?? "")}
                      onChangeText={(text) =>
                        setObjectField(fieldKey, "country", text)
                      }
                    />
                  </View>
                </View>
              </View>
            );
        }

        if (isJsonFieldType(type)) {
          return (
            <TextInput
              key={fieldKey}
              testID={`${testIDPrefix}-field-${fieldKey}`}
              label={label + (required ? " *" : "")}
              mode="outlined"
              disabled={disabled}
              multiline
              value={
                typeof values[fieldKey] === "string"
                  ? values[fieldKey]
                  : JSON.stringify(values[fieldKey] ?? {}, null, 2)
              }
              onChangeText={(text) => setValue(fieldKey, text)}
              placeholder={t("cms.forms.preview.jsonPlaceholder", "JSON object")}
            />
          );
        }

        if (type === "boolean" || type === "checkbox") {
          return (
            <View key={fieldKey} style={styles.checkboxRow}>
              <Checkbox
                testID={`${testIDPrefix}-checkbox-${fieldKey}`}
                status={values[fieldKey] ? "checked" : "unchecked"}
                disabled={disabled}
                onPress={() => setValue(fieldKey, !values[fieldKey])}
              />
              <Text>
                {label}
                {required ? " *" : ""}
              </Text>
              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        if (type === "select") {
          const options = extractOptions(raw);
          const current = values[fieldKey];
          const currentOption = options.find(
            (o) => String(o.value) === String(current ?? ""),
          );
          const menuVisible = !!openMenus[fieldKey];

          return (
            <View key={fieldKey} style={styles.fieldGroup}>
              <Text>
                {label}
                {required ? " *" : ""}
              </Text>

              {options.length === 0 ? (
                <Text variant="bodySmall">
                  {t("cms.forms.choiceNoOptions", "No options configured")}
                </Text>
              ) : (
                <Menu
                  visible={menuVisible}
                  onDismiss={() =>
                    setOpenMenus((prev) => ({ ...prev, [fieldKey]: false }))
                  }
                  anchor={
                    <Button
                      mode="outlined"
                      disabled={disabled}
                      onPress={() =>
                        setOpenMenus((prev) => ({ ...prev, [fieldKey]: true }))
                      }
                    >
                      {currentOption?.label ||
                        t("cms.forms.selectPlaceholder", "Select an option")}
                    </Button>
                  }
                >
                  {options.map((o) => {
                    const optionValue = String(o.value);
                    return (
                      <Menu.Item
                        key={optionValue}
                        title={String(o.label ?? optionValue)}
                        onPress={() => {
                          setValue(fieldKey, optionValue);
                          setOpenMenus((prev) => ({
                            ...prev,
                            [fieldKey]: false,
                          }));
                        }}
                      />
                    );
                  })}
                </Menu>
              )}

              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        if (isChoiceFieldType(type)) {
          const options = extractOptions(raw);
          const current = values[fieldKey];

          return (
            <View key={fieldKey} style={styles.fieldGroup}>
              <Text>
                {label}
                {required ? " *" : ""}
              </Text>

              {options.length === 0 ? (
                <Text variant="bodySmall">
                  {t("cms.forms.choiceNoOptions", "No options configured")}
                </Text>
              ) : (
                <View style={styles.choiceOptions}>
                  {options.map((o) => {
                    const optionValue = String(o.value);

                    const checked =
                      type === "checkbox_group" || type === "multiselect"
                        ? Array.isArray(current)
                          ? current.includes(optionValue)
                          : false
                        : current === optionValue;

                    return (
                      <View key={optionValue} style={styles.checkboxRow}>
                        <Checkbox
                          testID={`${testIDPrefix}-choice-${fieldKey}-${optionValue}`}
                          status={checked ? "checked" : "unchecked"}
                          disabled={disabled}
                          onPress={() => {
                            if (type === "checkbox_group" || type === "multiselect") {
                              const base = Array.isArray(current) ? current : [];
                              if (base.includes(optionValue)) {
                                setValue(
                                  fieldKey,
                                  base.filter((x) => x !== optionValue),
                                );
                                return;
                              }
                              setValue(fieldKey, [...base, optionValue]);
                              return;
                            }

                            setValue(
                              fieldKey,
                              current === optionValue ? undefined : optionValue,
                            );
                          }}
                        />
                        <Text>{String(o.label ?? optionValue)}</Text>
                      </View>
                    );
                  })}
                </View>
              )}

              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        if (type === "number" || type === "integer") {
          return (
            <View key={fieldKey} style={styles.fieldGroup}>
              <TextInput
                testID={`${testIDPrefix}-field-${fieldKey}`}
                label={label + (required ? " *" : "")}
                mode="outlined"
                disabled={disabled}
                value={String(values[fieldKey] ?? "")}
                onChangeText={(text) => setValue(fieldKey, text)}
                keyboardType="numeric"
                placeholder={rawAny?.placeholder}
              />
              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        if (type === "email" || type === "tel" || type === "date") {
          const keyboardType =
            type === "email"
              ? "email-address"
              : type === "tel"
                ? "phone-pad"
                : "default";

          const placeholder =
            rawAny?.placeholder ?? (type === "date" ? "YYYY-MM-DD" : undefined);

          return (
            <View key={fieldKey} style={styles.fieldGroup}>
              <TextInput
                testID={`${testIDPrefix}-field-${fieldKey}`}
                label={label + (required ? " *" : "")}
                mode="outlined"
                disabled={disabled}
                value={String(values[fieldKey] ?? "")}
                onChangeText={(text) => setValue(fieldKey, text)}
                keyboardType={keyboardType as any}
                autoCapitalize={type === "email" ? "none" : "sentences"}
                placeholder={placeholder}
              />
              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        if (isPhoneFieldType(type) || isDateTimeFieldType(type)) {
          const keyboardType = isPhoneFieldType(type) ? "phone-pad" : "default";
          const placeholder =
            rawAny?.placeholder ?? (isDateTimeFieldType(type) ? "YYYY-MM-DDTHH:MM" : undefined);

          return (
            <View key={fieldKey} style={styles.fieldGroup}>
              <TextInput
                testID={`${testIDPrefix}-field-${fieldKey}`}
                label={label + (required ? " *" : "")}
                mode="outlined"
                disabled={disabled}
                value={String(values[fieldKey] ?? "")}
                onChangeText={(text) => setValue(fieldKey, text)}
                keyboardType={keyboardType as any}
                placeholder={placeholder}
              />
              {helperText ? (
                <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
              ) : null}
              {errorText ? (
                <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
              ) : null}
            </View>
          );
        }

        return (
          <View key={fieldKey} style={styles.fieldGroup}>
            <TextInput
              testID={`${testIDPrefix}-field-${fieldKey}`}
              label={label + (required ? " *" : "")}
              mode="outlined"
              disabled={disabled}
              value={String(values[fieldKey] ?? "")}
              onChangeText={(text) => setValue(fieldKey, text)}
              multiline={type === "textarea"}
              placeholder={rawAny?.placeholder}
            />
            {helperText ? (
              <Text variant="bodySmall" style={styles.helperText}>{helperText}</Text>
            ) : null}
            {errorText ? (
              <Text variant="bodySmall" style={styles.errorText}>{errorText}</Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}
